import { useEffect, useRef, useState } from 'react';
import { games, type ArcadeResult, type ProgressNews } from '../net/api';
import { Sheet } from './Sheet';
import { Derby } from './Derby';
import { drawGhost } from '../art/ghost';
import { BombSquad, squadGameName, type Brand } from './BombSquad';

const CABINETS = [
  { key: 'trivia', name: 'Tide Trivia', blurb: 'Five quick questions about the world and the sea.', color: '#16808f' },
  { key: 'shell_shuffle', name: 'Shell Shuffle', blurb: 'Slide the shells back into order in as few moves as you can.', color: '#d4553e' },
  { key: 'pop_a_buoy', name: 'Pop-a-Buoy', blurb: 'Tap the buoys before they sink. 30 seconds!', color: '#e9a915' },
  { key: 'driftwood_derby', name: 'Driftwood Derby', blurb: 'Race up to 3 other players around the dunes. Multiplayer!', color: '#7a4f9a' },
] as const;

/** Haunted House cabinets (Haunted Harbor Halloween only). They pay candy as well as coins. */
const SPOOKY_CABINETS = [
  { key: 'ghost_pop', name: 'Ghost Pop', blurb: 'Tap the ghosts before they vanish. 30 seconds! Earns candy.', color: '#9b6ad6' },
  { key: 'spooky_trivia', name: 'Spooky Trivia', blurb: 'Five Halloween questions. Win to earn extra candy.', color: '#f08a24' },
] as const;

type Done = (r: ArcadeResult) => void;

export function Arcade({ mode, brand = 'bomb', joinSquad, onClose, onChanged }: { mode?: 'halloween'; brand?: Brand; joinSquad?: string; onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const squad = {
    key: 'bomb_squad',
    name: squadGameName(brand),
    blurb: brand === 'bubble' ? 'Find the hidden bubbles with number clues. Solo or with a friend!' : 'Find the hidden bombs with number clues. Solo or with a friend!',
    color: '#9b6ad6',
  };
  const list: readonly { key: string; name: string; blurb: string; color: string }[] = mode === 'halloween' ? SPOOKY_CABINETS : [...CABINETS, squad];
  const [game, setGame] = useState<string | undefined>(joinSquad ? 'bomb_squad' : undefined);
  const [result, setResult] = useState<ArcadeResult>();
  const done: Done = (r) => {
    setResult(r);
    onChanged(r.progress);
  };
  const cab = list.find((c) => c.key === game);
  return (
    <Sheet
      title={cab ? cab.name : mode === 'halloween' ? 'Spooky Games' : 'Bayside Arcade'}
      subtitle={cab ? <button className="link-btn" onClick={() => { setGame(undefined); setResult(undefined); }}>← All games</button> : mode === 'halloween' ? 'Only open during Haunted Harbor Halloween. Win candy for the Boo-tique!' : 'Pick a cabinet. Scores count toward the weekly leaderboards.'}
      onClose={onClose}
      wide
    >
      {!game && (
        <div className="cabinets">
          {list.map((c) => (
            <button key={c.key} className="cabinet" style={{ borderColor: c.color }} onClick={() => setGame(c.key)}>
              <strong style={{ color: c.color }}>{c.name}</strong>
              <span className="muted small-print">{c.blurb}</span>
              <span className="btn small">Play</span>
            </button>
          ))}
        </div>
      )}
      {game && game !== 'driftwood_derby' && game !== 'bomb_squad' && result && <Result r={result} onAgain={() => setResult(undefined)} />}
      {game === 'trivia' && !result && <Trivia game="trivia" onDone={done} />}
      {game === 'spooky_trivia' && !result && <Trivia game="spooky_trivia" onDone={done} />}
      {game === 'ghost_pop' && !result && <PopABuoy game="ghost_pop" spooky onDone={done} />}
      {game === 'shell_shuffle' && !result && <ShellShuffle onDone={done} />}
      {game === 'pop_a_buoy' && !result && <PopABuoy game="pop_a_buoy" onDone={done} />}
      {game === 'driftwood_derby' && <Derby onDone={onChanged} />}
      {game === 'bomb_squad' && <BombSquad brand={brand} joinRoomId={joinSquad} onDone={onChanged} />}
    </Sheet>
  );
}

function Result({ r, onAgain }: { r: ArcadeResult; onAgain: () => void }) {
  return (
    <div className="result">
      <strong className="big-score">{r.score} points</strong>
      {r.flagged ? (
        <p className="form-error">That run didn't look right, so it didn't count. Play normally and try again!</p>
      ) : (
        <p>{r.won ? 'Great game! ' : ''}+{r.coins} coins{r.candy ? ` · +${r.candy} candy` : ''} · +{r.xp} XP{r.leveledUp && <strong> · Level up! You're now level {r.level}.</strong>}</p>
      )}
      {r.dailyCapReached && <p className="muted small-print">You've reached today's coin limit for this game. XP still counts.</p>}
      <button className="btn" onClick={onAgain}>Play again</button>
    </div>
  );
}

function useStart<T>(game: string) {
  const [s, setS] = useState<(T & { sessionId: string }) | null>(null);
  const [error, setError] = useState<string>();
  const started = useRef(0);
  useEffect(() => {
    games.start<T>(game).then((x) => {
      started.current = performance.now();
      setS(x);
    }, (e: Error) => setError(e.message));
  }, [game]);
  return { s, error, started };
}

function Trivia({ game, onDone }: { game: string; onDone: Done }) {
  const { s, error } = useStart<{ questions: { q: string; choices: string[] }[] }>(game);
  const [answers, setAnswers] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  if (error) return <p className="form-error">{error}</p>;
  if (!s) return <p className="muted">Shuffling questions…</p>;
  const i = answers.length;
  const q = s.questions[i];
  async function pick(n: number) {
    const next = [...answers, n];
    setAnswers(next);
    if (next.length === s!.questions.length) {
      setBusy(true);
      try {
        onDone(await games.finish(s!.sessionId, { answers: next }));
      } finally {
        setBusy(false);
      }
    }
  }
  if (!q) return <p className="muted">{busy ? 'Checking answers…' : ''}</p>;
  return (
    <div className="trivia">
      <p className="muted small-print">Question {i + 1} of {s.questions.length}</p>
      <h3 className="trivia-q">{q.q}</h3>
      <div className="trivia-choices">
        {q.choices.map((c, n) => (
          <button key={n} className="btn ghost" onClick={() => void pick(n)}>{c}</button>
        ))}
      </div>
    </div>
  );
}

const SOLVED = '1,2,3,4,5,6,7,8,0';
function slide(board: number[], i: number) {
  const blank = board.indexOf(0);
  const near = [blank - 3, blank + 3, blank % 3 ? blank - 1 : -1, blank % 3 !== 2 ? blank + 1 : -1];
  if (!near.includes(i)) return null;
  const next = [...board];
  [next[blank], next[i]] = [next[i]!, next[blank]!];
  return next;
}

function ShellShuffle({ onDone }: { onDone: Done }) {
  const { s, error } = useStart<{ board: number[] }>('shell_shuffle');
  const [board, setBoard] = useState<number[]>();
  const [moves, setMoves] = useState<number[]>([]);
  useEffect(() => {
    if (s) setBoard(s.board);
  }, [s]);
  if (error) return <p className="form-error">{error}</p>;
  if (!s || !board) return <p className="muted">Shuffling shells…</p>;
  function tap(i: number) {
    const next = slide(board!, i);
    if (!next) return;
    const m = [...moves, i];
    setBoard(next);
    setMoves(m);
    if (next.join() === SOLVED) void games.finish(s!.sessionId, { moves: m }).then(onDone);
  }
  return (
    <div className="shuffle">
      <p className="muted small-print">Put the shells in order 1–8. Moves: {moves.length}</p>
      <div className="shuffle-grid" role="grid" aria-label="Sliding puzzle">
        {board.map((n, i) => (
          <button key={i} className={n ? 'shell' : 'shell empty'} disabled={!n} onClick={() => tap(i)} aria-label={n ? `Shell ${n}` : 'Empty'}>
            {n || ''}
          </button>
        ))}
      </div>
    </div>
  );
}

interface Target {
  id: number;
  x: number;
  y: number;
  at: number;
  ttl: number;
}

/** Pop-a-Buoy, or Ghost Pop with `spooky` (same rules, night-time ghosts). */
function PopABuoy({ game, spooky = false, onDone }: { game: string; spooky?: boolean; onDone: Done }) {
  const { s, error, started } = useStart<{ targets: Target[]; durationMs: number; width: number; height: number }>(game);
  const ref = useRef<HTMLCanvasElement>(null);
  const hits = useRef<{ id: number; t: number }[]>([]);
  const [score, setScore] = useState(0);
  const [left, setLeft] = useState(30);

  useEffect(() => {
    if (!s) return;
    const el = ref.current!;
    const dpr = window.devicePixelRatio || 1;
    el.width = s.width * dpr;
    el.height = s.height * dpr;
    const c = el.getContext('2d')!;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    let raf = 0;
    let finished = false;
    const draw = () => {
      const t = performance.now() - started.current;
      c.fillStyle = spooky ? '#1f1a33' : '#2f93a8';
      c.fillRect(0, 0, s.width, s.height);
      if (spooky) {
        c.fillStyle = '#f3e7b4';
        c.beginPath();
        c.arc(s.width - 70, 60, 34, 0, Math.PI * 2);
        c.fill();
        c.fillStyle = 'rgba(255,255,255,0.06)';
        for (let i = 0; i < 6; i++) c.fillRect(((i * 140 + t / 40) % (s.width + 200)) - 200, s.height - 60 - i * 12, 200, 18);
      } else {
        c.fillStyle = 'rgba(255,255,255,0.25)';
        for (let i = 0; i < 14; i++) c.fillRect(((i * 83 + t / 20) % (s.width + 40)) - 20, 20 + ((i * 53) % (s.height - 40)), 26, 2);
      }
      for (const g of s.targets) {
        if (t < g.at || t > g.at + g.ttl || hits.current.some((h) => h.id === g.id)) continue;
        const life = (t - g.at) / g.ttl;
        const bob = Math.sin(t / 120 + g.id) * 3;
        c.globalAlpha = life > 0.75 ? 1 - (life - 0.75) * 4 : 1;
        if (spooky) {
          drawGhost(c, g.x, g.y + bob, 24);
          c.globalAlpha = 1;
          continue;
        }
        c.fillStyle = '#d4553e';
        c.beginPath();
        c.arc(g.x, g.y + bob, 22, Math.PI, 0);
        c.fill();
        c.fillStyle = '#ffffff';
        c.beginPath();
        c.arc(g.x, g.y + bob, 22, 0, Math.PI);
        c.fill();
        c.fillStyle = '#e9a915';
        c.fillRect(g.x - 3, g.y + bob - 34, 6, 14);
        c.globalAlpha = 1;
      }
      setLeft(Math.max(0, Math.ceil((s.durationMs - t) / 1000)));
      if (t >= s.durationMs + 300 && !finished) {
        finished = true;
        void games.finish(s.sessionId, { hits: hits.current }).then(onDone);
        return;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [s]);

  if (error) return <p className="form-error">{error}</p>;
  if (!s) return <p className="muted">{spooky ? 'Waking up the ghosts…' : 'Setting out the buoys…'}</p>;
  return (
    <div className="buoys">
      <p className="muted small-print">Score {score} · {left}s left</p>
      <canvas
        ref={ref}
        className="buoy-canvas"
        style={{ aspectRatio: `${s.width} / ${s.height}` }}
        onPointerDown={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * s.width;
          const y = ((e.clientY - r.top) / r.height) * s.height;
          const t = performance.now() - started.current;
          const hit = s.targets.find((g) => t >= g.at && t <= g.at + g.ttl && Math.hypot(g.x - x, g.y - y) < 30 && !hits.current.some((h) => h.id === g.id));
          if (hit) {
            hits.current.push({ id: hit.id, t: Math.round(t) });
            setScore((n) => n + 50);
          }
        }}
        aria-label={spooky ? 'Ghost Pop: tap ghosts before they vanish' : 'Pop-a-Buoy: tap buoys before they sink'}
        role="img"
      />
    </div>
  );
}

